import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Input } from 'reactstrap';
import Select from 'react-select';
import queryString from 'query-string';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { programRequestsApi } from '../api/programRequestsApi';
import { InstitutionField } from '../shared/InstitutionField';
import { RadioCheckGroup } from 'components/academic-affair-report/components/RadioCheckGroup';
import { AdditionalCommentsField } from '../shared/AdditionalCommentsField';
import { createNewProgramModel } from './newProgramModel';
import {
    creditHoursError,
    hasNewProgramValidationErrors,
} from './newProgramValidation';
import {
    DELIVERY_MODE_OPTIONS,
    ON_CAMPUS_ONLINE_NATURE_OPTIONS,
} from './newProgramOptions';
import { createNewProgramFlow } from './createNewProgramFlow';

export const NewProgramForm = ({
    onBack,
    onSuccess,
    institutionOptions: parentInstitutionOptions = [],
    maximumAdditionalCommentsLength,
    initialModel,
    submitFlow = createNewProgramFlow,
    mode = 'create',
    requestDetails,
}) => {
    const [form, setForm] = useState(
        () => initialModel ?? createNewProgramModel()
    );
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [cipOptions, setCipOptions] = useState([]);
    const [degreeLevelOptions, setDegreeLevelOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [loadingCips, setLoadingCips] = useState(true);
    const [files, setFiles] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (parentInstitutionOptions.length > 0) {
            setInstitutionOptions(parentInstitutionOptions);
        } else {
            // Service returns { label: id, value: name }; remap to { value: id, label: name }
            programRequestsApi.getInstitutions().then(res => {
                const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
                setInstitutionOptions(mapped);
            });
        }

        setLoadingCips(true);
        programRequestsApi.getCipCodes().then(res => {
            const mapped = (res ?? []).map(x => ({
                value: x.cipCode,
                label: `${x.cipCode} - ${x.cipName}`,
            }));
            setCipOptions(mapped);
            setLoadingCips(false);
        });

        // Service returns { label: id, value: name }; remap to { value: id, label: name }
        programRequestsApi.getDegreeLevels().then(res => {
            const mapped = (res ?? []).map(x => ({ value: x.label, label: x.value }));
            setDegreeLevelOptions(mapped);
        });
    }, []);

    // Auto-select when only one institution is available
    useEffect(() => {
        if (institutionOptions.length === 1 && form.institutionId === null) {
            setForm(f => ({ ...f, institutionId: institutionOptions[0].value }));
        }
    }, [institutionOptions]);

    // Fetch locations whenever the selected institution changes
    useEffect(() => {
        if (!form.institutionId) {
            setLocationOptions([]);
            setForm(f => ({ ...f, locations: [] }));
            return;
        }
        if (mode !== 'edit') {
            setForm(f => ({ ...f, locations: [] }));
        }
        const query = queryString.stringify(
            { institutions: form.institutionId },
            { arrayFormat: 'separator', skipEmptyString: true },
        );
        programRequestsApi.getLocations(query).then(res => {
            const mapped = (res?.locations ?? []).map(x => ({ value: x.key, label: x.value }));
            setLocationOptions(mapped);
            if (mode !== 'edit' && mapped.length === 1) {
                setForm(f => ({ ...f, locations: mapped }));
            }
        });
    }, [form.institutionId, mode]);

    const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));

    // RadioCheckGroup calls onChange({ [name]: value }) where value is '' when toggling off
    const handleRadioChange = key => obj => {
        const raw = obj[key];
        setField(key, raw === '' ? null : raw);
    };

    const handleDeliveryModeChange = obj => {
        const mode = obj.deliveryMode === '' ? null : obj.deliveryMode;
        // Modes 1 and 2 have a fixed nature; mode 3 lets the user choose
        const nature = mode === 1 ? 0 : mode === 2 ? 1 : null;
        setForm(f => ({ ...f, deliveryMode: mode, onlineNature: nature }));
    };

    const handleSubmit = () => {
        setSubmitted(true);
        setSubmitError(null);
        if (hasNewProgramValidationErrors(form, maximumAdditionalCommentsLength)) {
            return;
        }

        setSubmitting(true);

        const onError = err => {
            const message =
                err?.response?.data?.message ?? 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
            setSubmitting(false);
        };

        submitFlow({ form, files, details: requestDetails, onError }).then(succeeded => {
            setSubmitting(false);
            if (succeeded) onSuccess();
        });
    };

    const err = msg => <p className="text-danger mt-1">{msg}</p>;

    return (
        <div>
            {mode !== 'edit' && (
                <button type="button" className="btn btn-link p-0 mb-3" onClick={onBack}>
                    ← Back
                </button>
            )}

            <InstitutionField
                institutionOptions={institutionOptions}
                value={form.institutionId}
                onChange={val => setField('institutionId', val)}
            />
            {submitted && !form.institutionId && err('Institution is required.')}

            <FormGroup>
                <Label for="create-program-name">
                    Program Name <sup className="text-danger">*</sup>
                </Label>
                <Input
                    id="create-program-name"
                    type="text"
                    placeholder="Program Name"
                    value={form.programName}
                    onChange={e => setField('programName', e.target.value)}
                />
                {submitted && !form.programName?.trim() && err('Program Name is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="create-cip-code">
                    CIP Code <sup className="text-danger">*</sup>
                </Label>
                <Select
                    inputId="create-cip-code"
                    options={cipOptions}
                    value={cipOptions.find(o => o.value === form.cipCode) ?? null}
                    onChange={opt => setField('cipCode', opt?.value ?? null)}
                    placeholder="Search CIP codes..."
                    isClearable
                    isLoading={loadingCips}
                />
                {submitted && !form.cipCode && err('CIP Code is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="create-locations">
                    Locations <sup className="text-danger">*</sup>
                </Label>
                {locationOptions.length === 1 ? (
                    <div className="form-control-plaintext">{locationOptions[0].label}</div>
                ) : (
                    <Select
                        inputId="create-locations"
                        isMulti
                        options={locationOptions}
                        value={form.locations}
                        onChange={selected => setField('locations', selected ?? [])}
                        placeholder={
                            form.institutionId ? 'Select locations...' : 'Select an institution first'
                        }
                        isDisabled={!form.institutionId}
                        isClearable
                    />
                )}
                {submitted && !form.locations?.length && err('At least one Location is required.')}
            </FormGroup>

            <FormGroup>
                <Label for="create-degree-level">
                    Degree / Award Level <sup className="text-danger">*</sup>
                </Label>
                <Select
                    inputId="create-degree-level"
                    options={degreeLevelOptions}
                    value={degreeLevelOptions.find(o => o.value === form.degreeLevel) ?? null}
                    onChange={opt => setField('degreeLevel', opt?.value ?? null)}
                    placeholder="Select degree level..."
                    isClearable
                />
                {submitted && !form.degreeLevel && err('Degree/Award Level is required.')}
            </FormGroup>

            <FormGroup>
                <div className="row">
                    <div className="col-md-6">
                        <Label className="d-block">
                            Delivery Mode <sup className="text-danger">*</sup>
                        </Label>
                        <RadioCheckGroup
                            onChange={handleDeliveryModeChange}
                            name="deliveryMode"
                            value={form.deliveryMode}
                            options={DELIVERY_MODE_OPTIONS}
                        />
                        {submitted && form.deliveryMode === null && err('Delivery Mode is required.')}
                    </div>
                    {form.deliveryMode !== null && (
                        <div className="col-md-6 mt-3 mt-md-0">
                            <Label className="d-block">
                                Nature of Online Program
                                {form.deliveryMode === 3 && <sup className="text-danger"> *</sup>}
                            </Label>
                            {form.deliveryMode === 1 && (
                                <div className="form-control-plaintext">None</div>
                            )}
                            {form.deliveryMode === 2 && (
                                <div className="form-control-plaintext">100% Online</div>
                            )}
                            {form.deliveryMode === 3 && (
                                <>
                                    <RadioCheckGroup
                                        onChange={handleRadioChange('onlineNature')}
                                        name="onlineNature"
                                        value={form.onlineNature}
                                        options={ON_CAMPUS_ONLINE_NATURE_OPTIONS}
                                    />
                                    {submitted && form.onlineNature === null &&
                                        err('Nature of Online Program is required.')}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </FormGroup>

            <FormGroup>
                <Label>Credit Hours <sup className="text-danger">*</sup></Label>
                <div className="d-flex" style={{ gap: '1rem' }}>
                    <div className="flex-fill">
                        <Label for="create-credit-min" className="small text-muted mb-1">
                            Min
                        </Label>
                        <Input
                            id="create-credit-min"
                            type="number"
                            placeholder="Min"
                            value={form.creditMin}
                            onChange={e => setField('creditMin', e.target.value)}
                            min="0"
                        />
                    </div>
                    <div className="flex-fill">
                        <Label for="create-credit-max" className="small text-muted mb-1">
                            Max
                        </Label>
                        <Input
                            id="create-credit-max"
                            type="number"
                            placeholder="Max"
                            value={form.creditMax}
                            onChange={e => setField('creditMax', e.target.value)}
                            min="0"
                        />
                    </div>
                </div>
                {submitted && creditHoursError(form) && err(creditHoursError(form))}
            </FormGroup>

            <FormGroup>
                <Label>Support Documentation</Label>
                <div className="w-100 hidden-file-input-small">
                    <input
                        type="file"
                        accept=".csv,.zip,.pdf,.xls,.xlsx"
                        onChange={e => setFiles(e.target.files)}
                        multiple
                    />
                    <button type="button" className="btn btn-outline-primary btn-sm">
                        <FontAwesomeIcon icon={faUpload} className="mr-1" /> Upload
                    </button>
                </div>
                {files &&
                    Array.from(files).map((f, i) => (
                        <div key={i} className="mt-1 small text-muted">
                            <FontAwesomeIcon icon={faPaperclip} className="mr-1" />
                            {f.name}
                        </div>
                    ))}
            </FormGroup>

            <AdditionalCommentsField
                id="create-comments"
                value={form.comments}
                onChange={value => setField('comments', value)}
                maximumLength={maximumAdditionalCommentsLength}
                submitted={submitted}
            />

            {submitError && (
                <div className="alert alert-danger mt-3" role="alert">
                    {submitError}
                </div>
            )}

            <div className="d-flex justify-content-end mt-4">
                <Button color="secondary" className="mr-2" onClick={onBack} disabled={submitting}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={submitting}>
                    {submitting
                        ? mode === 'edit' ? 'Saving…' : 'Submitting…'
                        : mode === 'edit' ? 'Save Changes' : 'Submit Request'}
                </Button>
            </div>
        </div>
    );
};
